import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import { 
  Mail, 
  Search, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock,
  ExternalLink,
  Send,
  ChevronLeft,
  ChevronRight,
  Filter,
  BarChart3
} from 'lucide-react';

const API_URL = API_BASE || '';

// Email type labels
const EMAIL_TYPE_LABELS = {
  order_confirmation: 'Order Confirmation',
  delivery_notice: 'Delivery Notice',
  invoice: 'Invoice',
  password_reset: 'Password Reset',
  partner_invitation: 'Partner Invitation',
  upload_link: 'Upload Link',
  test_email: 'Test Email',
  welcome: 'Welcome',
  ncb_report: 'NCB Report',
  quote_ready: 'Quote Ready',
  other: 'Other'
};

// Status badge colors
const getStatusBadge = (status) => {
  switch (status) {
    case 'sent':
      return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Sent</Badge>;
    case 'failed':
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
    case 'pending':
      return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/* Embeddable panel for System Settings integration */
export function EmailLogPanel() {
  return <EmailLogContent embedded />;
}

export default function EmailLogPage() {
  return <EmailLogContent />;
}

function EmailLogContent({ embedded = false }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  // Detail dialog
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Resend dialog
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [resendEvent, setResendEvent] = useState(null);
  const [resendOverride, setResendOverride] = useState('');
  const [resending, setResending] = useState(false);
  
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '50'
      });
      
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('email_type', typeFilter);
      
      const response = await fetch(`${API_URL}/api/admin/email-events?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch email events');
      
      const data = await response.json();
      setEvents(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching email events:', error);
      toast.error('Failed to load email events');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, typeFilter]);
  
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/email-events/stats/summary?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  useEffect(() => {
    fetchEvents();
    fetchStats();
  }, [fetchEvents]);
  
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEvents();
  };
  
  const openEventDetail = async (eventId) => {
    setDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/email-events/${eventId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch event details');
      
      const data = await response.json();
      setSelectedEvent(data);
    } catch (error) {
      console.error('Error fetching event details:', error);
      toast.error('Failed to load email details');
    } finally {
      setDetailLoading(false);
    }
  };
  
  const openResendDialog = (event) => {
    setResendEvent(event);
    setResendOverride('');
    setResendDialogOpen(true);
  };
  
  const handleResend = async () => {
    if (!resendEvent) return;
    
    setResending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/email-events/${resendEvent.id}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          override_recipient: resendOverride || null
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend email');
      }
      
      toast.success(data.message || 'Email resent successfully');
      setResendDialogOpen(false);
      fetchEvents();
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error(error.message || 'Failed to resend email');
    } finally {
      setResending(false);
    }
  };
  
  return (
    <div className={embedded ? "" : "min-h-screen bg-background p-6"} data-testid="email-log-page">
      <div className={embedded ? "space-y-4" : "max-w-7xl mx-auto space-y-6"}>
        {/* Header */}
        <div className="flex items-center justify-between">
          {!embedded && (
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Mail className="w-6 h-6" />
                Email Log
              </h1>
              <p className="text-muted-foreground mt-1">
                Track and manage all outgoing system emails
              </p>
            </div>
          )}
          <Button onClick={() => { fetchEvents(); fetchStats(); }} variant="outline" size={embedded ? "sm" : "default"} data-testid="refresh-btn" className={embedded ? "ml-auto" : ""}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Emails (30d)</p>
                    <p className="text-2xl font-bold">{stats.total_emails}</p>
                  </div>
                  <Mail className="w-8 h-8 text-muted-foreground/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Sent Successfully</p>
                    <p className="text-2xl font-bold text-green-600">{stats.sent_count}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Failed</p>
                    <p className="text-2xl font-bold text-red-600">{stats.failed_count}</p>
                  </div>
                  <XCircle className="w-8 h-8 text-red-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Success Rate</p>
                    <p className="text-2xl font-bold">{stats.success_rate}%</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-muted-foreground/30" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by email, subject, or order..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="search-input"
                  />
                </div>
              </div>
              
              <div className="w-[150px]">
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Status</label>
                <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-[180px]">
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Type</label>
                <Select value={typeFilter || "all"} onValueChange={(v) => { setTypeFilter(v === "all" ? "" : v); setPage(1); }}>
                  <SelectTrigger data-testid="type-filter">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {Object.entries(EMAIL_TYPE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button type="submit" data-testid="apply-filters-btn">
                <Filter className="w-4 h-4 mr-2" />
                Apply
              </Button>
            </form>
          </CardContent>
        </Card>
        
        {/* Events Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Email Events</CardTitle>
            <CardDescription>{total} total emails</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No email events found</p>
                <p className="text-sm mt-1">Emails will appear here once sent from the system</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Time</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Recipient</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Type</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Subject</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Related</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event) => (
                        <tr key={event.id} className="border-b hover:bg-muted/50 transition-colors">
                          <td className="py-3 px-2 text-sm whitespace-nowrap">
                            {formatDate(event.timestamp)}
                          </td>
                          <td className="py-3 px-2 text-sm">
                            <span className="font-medium">{event.recipient_email}</span>
                            {event.mode === 'DEV' && (
                              <Badge variant="outline" className="ml-2 text-xs">DEV</Badge>
                            )}
                          </td>
                          <td className="py-3 px-2 text-sm">
                            <Badge variant="secondary">
                              {EMAIL_TYPE_LABELS[event.email_type] || event.email_type}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-sm max-w-[250px] truncate" title={event.subject}>
                            {event.subject}
                          </td>
                          <td className="py-3 px-2 text-sm">
                            {event.related_label ? (
                              <button
                                onClick={() => event.related_type === 'order' && navigate(`/orders/${event.related_id}`)}
                                className="text-primary hover:underline flex items-center gap-1"
                                data-testid={`related-link-${event.id}`}
                              >
                                {event.related_label}
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {getStatusBadge(event.status)}
                            {event.error_message && (
                              <p className="text-xs text-red-500 mt-1 max-w-[150px] truncate" title={event.error_message}>
                                {event.error_message}
                              </p>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openEventDetail(event.id)}
                                data-testid={`view-details-${event.id}`}
                              >
                                View
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => openResendDialog(event)}
                                data-testid={`resend-${event.id}`}
                              >
                                <Send className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Event Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Details</DialogTitle>
            <DialogDescription>
              {selectedEvent?.subject}
            </DialogDescription>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin" />
            </div>
          ) : selectedEvent && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Recipient</p>
                  <p className="text-sm">{selectedEvent.recipient_email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedEvent.status)}</div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Type</p>
                  <p className="text-sm">{EMAIL_TYPE_LABELS[selectedEvent.email_type] || selectedEvent.email_type}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Sent At</p>
                  <p className="text-sm">{formatDate(selectedEvent.timestamp)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Mode</p>
                  <p className="text-sm">{selectedEvent.mode || 'SMTP'} ({selectedEvent.smtp_source || 'unknown'})</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Related</p>
                  <p className="text-sm">{selectedEvent.related_label || '-'}</p>
                </div>
              </div>
              
              {selectedEvent.error_message && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-800">Error</p>
                  <p className="text-sm text-red-600">{selectedEvent.error_message}</p>
                </div>
              )}
              
              {selectedEvent.resend_count > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-800">Resend History</p>
                  <p className="text-sm text-blue-600">
                    Resent {selectedEvent.resend_count} time(s). Last resent: {formatDate(selectedEvent.last_resent_at)} by {selectedEvent.last_resent_by}
                  </p>
                </div>
              )}
              
              {selectedEvent.html_body && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Email Preview</p>
                  <div 
                    className="border rounded-lg p-4 bg-white max-h-[300px] overflow-y-auto text-sm"
                    dangerouslySetInnerHTML={{ __html: selectedEvent.html_body }}
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedEvent(null)}>
              Close
            </Button>
            {selectedEvent && (
              <Button onClick={() => { setSelectedEvent(null); openResendDialog(selectedEvent); }}>
                <Send className="w-4 h-4 mr-2" />
                Resend
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Resend Dialog */}
      <Dialog open={resendDialogOpen} onOpenChange={setResendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resend Email</DialogTitle>
            <DialogDescription>
              Resend this email to the original recipient or specify a different email address.
            </DialogDescription>
          </DialogHeader>
          
          {resendEvent && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium">Subject</p>
                <p className="text-sm text-muted-foreground">{resendEvent.subject}</p>
              </div>
              
              <div>
                <p className="text-sm font-medium">Original Recipient</p>
                <p className="text-sm text-muted-foreground">{resendEvent.recipient_email}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium">Override Recipient (optional)</label>
                <Input
                  placeholder="Enter different email address..."
                  value={resendOverride}
                  onChange={(e) => setResendOverride(e.target.value)}
                  className="mt-1"
                  data-testid="resend-override-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave empty to send to the original recipient
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleResend} disabled={resending} data-testid="confirm-resend-btn">
              {resending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Resend Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
